import React, { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ArrowRightLeft, X, Check } from "lucide-react";
import Modal from "components/common/Modal";
import betaAPI from "v2/api";

const capitalizeWords = (str) =>
  str
    ?.split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ") || "";

const SwitchTeamModal = ({
  show,
  onClose,
  onSwitch,
  player,
  currentTeamId,
  workspaceId,
}) => {
  const { t } = useTranslation();
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch teams when modal opens
  useEffect(() => {
    if (show && workspaceId) {
      const fetchTeams = async () => {
        setLoading(true);
        try {
          const response = await betaAPI.getDashboard({ workspaceId });
          // teams is paginated: { results: [...], page, page_size, ... }
          const teamsList =
            response.data?.teams?.results || response.data?.teams || [];
          // Filter out the current team
          setTeams(
            teamsList.filter((tm) => String(tm.id) !== String(currentTeamId))
          );
        } catch (err) {
          console.error("Failed to fetch teams:", err);
        } finally {
          setLoading(false);
        }
      };
      fetchTeams();
    }
  }, [show, workspaceId, currentTeamId]);

  const handleClose = () => {
    setSelectedTeamId(null);
    onClose();
  };

  const handleSwitch = () => {
    if (!selectedTeamId) return;
    onSwitch?.(selectedTeamId);
    handleClose();
  };

  const selectedTeam = teams.find((tm) => tm.id === selectedTeamId);

  return (
    <Modal show={show} onClose={handleClose} maxWidth="480px">
      {/* Header */}
      <div
        className="relative overflow-hidden p-7"
        style={{
          background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
        }}
      >
        {/* Decorative circles */}
        <div
          className="absolute rounded-full"
          style={{
            width: 120,
            height: 120,
            top: -30,
            right: -30,
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 80,
            height: 80,
            bottom: -40,
            right: 60,
            background: "rgba(255, 255, 255, 0.08)",
          }}
        />

        {/* Header content */}
        <div className="relative flex items-start justify-between">
          <div>
            {/* Icon */}
            <div
              className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <ArrowRightLeft size={24} className="text-white" />
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold text-white">
              {t("workspace.switchTeamModal.title")}
            </h2>
            <p
              className="mt-1.5 text-sm"
              style={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              <Trans
                i18nKey="workspace.switchTeamModal.subtitle"
                values={{ name: player?.name ?? "" }}
                components={{ bold: <span className="font-medium" /> }}
              />
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={handleClose}
            className="flex h-9 w-9 items-center justify-center rounded-[10px] transition-colors duration-150"
            style={{ background: "rgba(255, 255, 255, 0.2)" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)")
            }
          >
            <X size={18} className="text-white" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-7">
        <label className="mb-3 block text-sm font-medium text-[#374151]">
          {t("workspace.switchTeamModal.selectLabel")}
        </label>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="border-t-transparent h-6 w-6 animate-spin rounded-full border-2 border-[#6366F1]" />
          </div>
        ) : teams.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm text-[#6B7280]">
              {t("workspace.switchTeamModal.emptyTitle")}
            </p>
            <p className="mt-1 text-xs text-[#9CA3AF]">
              {t("workspace.switchTeamModal.emptyDescription")}
            </p>
          </div>
        ) : (
          <div className="max-h-[280px] space-y-2 overflow-y-auto">
            {teams.map((team) => {
              const isSelected = team.id === selectedTeamId;
              return (
                <button
                  key={team.id}
                  onClick={() => setSelectedTeamId(team.id)}
                  className={`flex w-full items-center justify-between rounded-xl border p-3 text-left transition duration-150 ${
                    isSelected
                      ? "border-[#6366F1] bg-[#EEF2FF]"
                      : "border-[#E5E7EB] hover:border-[#D1D5DB] hover:bg-[#F9FAFB]"
                  }`}
                >
                  <div>
                    <p
                      className={`text-sm font-medium ${
                        isSelected ? "text-[#4F46E5]" : "text-[#111827]"
                      }`}
                    >
                      {team.name}
                    </p>
                    <p className="text-xs text-[#6B7280]">
                      {team.sport
                        ? t("workspace.switchTeamModal.teamMetaWithSport", {
                            count: team.player_count || 0,
                            sport: capitalizeWords(team.sport),
                          })
                        : t("workspace.switchTeamModal.teamMeta", {
                            count: team.player_count || 0,
                          })}
                    </p>
                  </div>
                  {isSelected && (
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#6366F1]">
                      <Check size={14} className="text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 border-t border-[#F3F4F6] px-7 py-5">
        <button
          onClick={handleClose}
          className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition-colors duration-150 hover:bg-[#F9FAFB]"
        >
          {t("common.actions.cancel")}
        </button>
        <button
          onClick={handleSwitch}
          disabled={!selectedTeamId}
          className="rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-150"
          style={
            selectedTeamId
              ? {
                  background:
                    "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
                  boxShadow: "0 2px 8px rgba(99, 102, 241, 0.3)",
                  cursor: "pointer",
                }
              : {
                  background: "#F3F4F6",
                  color: "#9CA3AF",
                  cursor: "not-allowed",
                }
          }
        >
          {t("workspace.switchTeamModal.submit", {
            name:
              selectedTeam?.name || t("workspace.switchTeamModal.teamFallback"),
          })}
        </button>
      </div>
    </Modal>
  );
};

export default SwitchTeamModal;
